import {useMemo, useCallback, useState, useEffect} from 'react'

import {Heading, Pagination, Button} from '@primer/react'

import {useSearchParams} from '@github-ui/react-router'

import type {ServersData, BaseServer} from '../../../../types'

import {GridSection} from '../GridSection/GridSection'

import styles from './Content.module.css'

type Props = {
  data: ServersData
  isOssV01ApiEnabled: boolean
}

export function Content({data, isOssV01ApiEnabled}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [cursorHistory, setCursorHistory] = useState<string[]>([''])

  // Get search parameter based on API version
  const searchParamKey = isOssV01ApiEnabled ? 'search' : 'q'
  const urlSearchQuery = searchParams.get(searchParamKey) || ''
  const hasSearchTerm = urlSearchQuery.trim().length > 0

  // Pagination parameters based on API version
  const currentPage = isOssV01ApiEnabled ? parseInt(searchParams.get('page') || '1', 10) : 1
  const currentCursor = isOssV01ApiEnabled ? '' : searchParams.get('cursor') || ''

  // Track cursor history for v0 API pagination
  useEffect(() => {
    if (!isOssV01ApiEnabled && currentCursor && !cursorHistory.includes(currentCursor)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCursorHistory(prev => [...prev, currentCursor])
    }
  }, [currentCursor, cursorHistory, isOssV01ApiEnabled])

  // Reset cursor history when search changes for v0 API
  useEffect(() => {
    if (!isOssV01ApiEnabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCursorHistory([''])
    }
  }, [urlSearchQuery, isOssV01ApiEnabled])

  // Memoized server normalization function
  const normalizeServer = useCallback(
    (server: BaseServer) => ({
      ...server,
      uses_custom_opengraph_image: server.uses_custom_opengraph_image || false,
    }),
    [],
  )

  // Transform and filter MCPs based on search state
  const {regularMcps, searchResults} = useMemo(() => {
    // Only use the data if it matches the current search state
    // This prevents showing stale search results when clearing the search
    const transformedMcps = (data.servers || []).map(normalizeServer)

    if (!hasSearchTerm) {
      return {
        regularMcps: transformedMcps,
        searchResults: [],
      }
    }

    // When searching, use server-provided results directly (no client-side filtering)
    return {
      regularMcps: [],
      searchResults: transformedMcps,
    }
  }, [data.servers, hasSearchTerm, normalizeServer])

  // Determine which MCPs to display
  const currentMcps = hasSearchTerm ? searchResults : regularMcps

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(current => {
        const newSearchParams = new URLSearchParams(current)
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            newSearchParams.delete(key)
          } else {
            newSearchParams.set(key, value)
          }
        }
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  // v0.1 API page-based pagination handler
  const handlePageChange = useCallback(
    (event: React.MouseEvent, page: number) => {
      event.preventDefault()
      updateSearchParams({page: page.toString()})
    },
    [updateSearchParams],
  )

  // v0 API cursor-based pagination handlers
  const handleNextPage = useCallback(() => {
    if (data.metadata.next_cursor) {
      updateSearchParams({cursor: data.metadata.next_cursor})
    }
  }, [data.metadata.next_cursor, updateSearchParams])

  const handlePreviousPage = useCallback(() => {
    const currentIndex = cursorHistory.indexOf(currentCursor)
    if (currentIndex > 0) {
      const previousCursor = cursorHistory[currentIndex - 1]
      if (previousCursor === '' || previousCursor === undefined) {
        updateSearchParams({cursor: null})
      } else {
        updateSearchParams({cursor: previousCursor})
      }
    }
  }, [cursorHistory, currentCursor, updateSearchParams])

  // Pagination state
  const totalPages = data.metadata.total_pages || 1
  const currentIndex = cursorHistory.indexOf(currentCursor)
  const hasNextPage = !!data.metadata.next_cursor
  const hasPreviousPage = currentIndex > 0

  const heading = hasSearchTerm ? 'Search results' : 'All MCP servers'

  return (
    <div className={styles.container}>
      <div>
        {currentMcps.length > 0 ? (
          <div className="d-flex flex-items-center mb-4">
            <Heading as="h2" className="f3 mr-2">
              {heading}
            </Heading>

            <span className="Counter">{hasSearchTerm ? data.metadata.count : data.metadata.total}</span>
          </div>
        ) : (
          <Heading as="h2" className="f3 mb-4">
            {heading}
          </Heading>
        )}

        {currentMcps.length > 0 ? <GridSection mcps={currentMcps} /> : null}

        {hasSearchTerm && searchResults.length === 0 && (
          <p className="color-fg-muted mb-0">
            No MCPs found matching &quot;{urlSearchQuery}&quot;. Try a different search term.
          </p>
        )}
      </div>

      {/* v0.1 API: page-based pagination */}
      {isOssV01ApiEnabled && totalPages > 1 && (
        <div className={styles.pagination}>
          <Pagination
            pageCount={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            surroundingPageCount={2}
            aria-label="MCP Registry pagination"
          />
        </div>
      )}

      {/* v0 API: cursor-based pagination */}
      {!isOssV01ApiEnabled && (hasNextPage || hasPreviousPage) && (
        <div className={styles.pagination}>
          <Button variant="invisible" size="small" disabled={!hasPreviousPage} onClick={handlePreviousPage}>
            Previous
          </Button>
          <Button variant="invisible" size="small" disabled={!hasNextPage} onClick={handleNextPage}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

try{ Content.displayName ||= 'Content' } catch {}